// Re-implementations of the Webflow IX2 interactions used on the site. Purely visual
// animations (marquees, fade-ins) live in src/styles/interactions.css, keyed on data-ix.

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { animateHeight, type WfProps } from './element';

// --- Dismissible banners ("Banner 10 [Close]", "Close Notification Banner") --------------------

const DismissContext = createContext<(() => void) | null>(null);

export function Dismissible({
  as: Tag = 'div',
  mode,
  style,
  children,
  ...props
}: WfProps & { mode: 'hide' | 'collapse' }) {
  const [dismissed, setDismissed] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const dismiss = () => {
    if (mode === 'collapse' && ref.current) {
      ref.current.style.overflow = 'hidden';
      animateHeight(ref.current, false, 500, 'ease-out');
    } else setDismissed(true);
  };
  return (
    <DismissContext.Provider value={dismiss}>
      <Tag ref={ref} {...props} style={dismissed ? { ...style, display: 'none' } : style}>
        {children}
      </Tag>
    </DismissContext.Provider>
  );
}

export function DismissTrigger({ as: Tag = 'div', children, ...props }: WfProps) {
  const dismiss = useContext(DismissContext);
  return (
    <Tag
      {...props}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        dismiss?.();
      }}
    >
      {children}
    </Tag>
  );
}

// --- FAQ accordion ("FAQ 4 accordion [Open]/[Close]") -----------------------------------------

/** `toggled` turns true on first use: until then the markup matches Webflow's exactly. */
const FaqContext = createContext<{ open: boolean; toggled: boolean; toggle: () => void } | null>(null);

export function FaqAccordion({ as: Tag = 'div', children, ...props }: WfProps) {
  const [open, setOpen] = useState(false);
  const [toggled, setToggled] = useState(false);
  const toggle = () => {
    setToggled(true);
    setOpen((o) => !o);
  };
  return (
    <FaqContext.Provider value={{ open, toggled, toggle }}>
      <Tag {...props}>{children}</Tag>
    </FaqContext.Provider>
  );
}

export function FaqQuestion({ as: Tag = 'div', children, ...props }: WfProps) {
  const faq = useContext(FaqContext);
  return (
    <Tag {...props} role="button" tabIndex={0} aria-expanded={faq?.open ?? false} onClick={() => faq?.toggle()}>
      {children}
    </Tag>
  );
}

export function FaqAnswer({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const faq = useContext(FaqContext);
  const ref = useRef<HTMLElement>(null);
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (ref.current) animateHeight(ref.current, faq?.open ?? false, 400);
  }, [faq?.open]);
  // Closed answers start collapsed (the interaction's initial state).
  return (
    <Tag ref={ref} {...props} style={{ ...style, height: '0px', width: '100%' }}>
      {children}
    </Tag>
  );
}

export function FaqIcon({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const faq = useContext(FaqContext);
  return (
    <Tag
      {...props}
      style={
        faq?.toggled
          ? { ...style, transform: `rotateZ(${faq?.open ? 45 : 0}deg)`, transition: 'transform 200ms ease' }
          : style
      }
    >
      {children}
    </Tag>
  );
}
